import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Camera, Film, Music, Sparkles, CheckCircle2, ArrowRight, Zap, Edit } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const MediaProductionPage = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const features = [
    {
      icon: <Video className="w-8 h-8" />,
      title: "Professional Video Production",
      description: "High-quality video content that converts",
      detail: "From concept to final cut — we produce professional videos for social media, websites, ads, and campaigns. Engaging, on-brand, and optimized for every platform."
    },
    {
      icon: <Camera className="w-8 h-8" />,
      title: "Photography & Product Shoots",
      description: "Stunning visuals that sell",
      detail: "Professional photography for products, services, team headshots, and brand imagery. High-resolution, edited, and ready for all marketing channels."
    },
    {
      icon: <Edit className="w-8 h-8" />,
      title: "Video Editing & Post-Production",
      description: "Polish raw footage into cinematic content",
      detail: "We take your raw footage and transform it into polished, professional videos with color grading, sound design, motion graphics, and effects."
    },
    {
      icon: <Film className="w-8 h-8" />,
      title: "Social Media Reels & Short-Form Content",
      description: "Scroll-stopping content for TikTok, Instagram, YouTube Shorts",
      detail: "We create engaging short-form videos optimized for social algorithms. Fast-paced, attention-grabbing, and designed to go viral."
    },
    {
      icon: <Music className="w-8 h-8" />,
      title: "Motion Graphics & Animation",
      description: "Bring your brand to life",
      detail: "From animated logos to explainer videos and infographics — we create eye-catching motion graphics that simplify complex ideas and captivate audiences."
    }
  ];

  const process = [
    {
      step: "01",
      title: "Creative Brief & Concept Development",
      description: "We discuss your goals, target audience, and creative vision. We develop a concept that aligns with your brand and delivers results."
    },
    {
      step: "02",
      title: "Pre-Production Planning",
      description: "We handle storyboarding, scripting, shot lists, location scouting, talent coordination, and scheduling. Everything planned to perfection."
    },
    {
      step: "03",
      title: "Production & Shoot Day",
      description: "Our team captures high-quality footage using professional equipment. We direct, film, photograph, and ensure every shot is perfect."
    },
    {
      step: "04",
      title: "Post-Production & Editing",
      description: "We edit, color grade, add music, sound effects, motion graphics, and polish your content into a final masterpiece."
    },
    {
      step: "05",
      title: "Delivery & Optimization",
      description: "You receive final files in all required formats, optimized for web, social, and any platform you need. Ready to publish."
    }
  ];

  const benefits = [
    "Stand out with professional, high-quality visual content",
    "Increase engagement and conversions with video and photography",
    "Save time — we handle everything from concept to delivery",
    "Build brand credibility with polished, cinematic production",
    "Maximize ROI with content optimized for every platform"
  ];

  const idealFor = [
    "Businesses launching new products or services",
    "Brands needing consistent social media video content",
    "E-commerce brands requiring product photography",
    "Companies wanting professional team or office photography",
    "Businesses looking to scale their video marketing efforts"
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header onContactClick={scrollToContact} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-brand-brown/20 to-black"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-brand-maroon/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-brand-olive/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-brand-maroon/20 rounded-full border border-brand-maroon/40 mb-6">
              <span className="text-brand-maroon font-bold">SERVICES</span>
            </div>
            <h1 className="text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">
              Media{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon via-brand-olive to-brand-maroon drop-shadow-[0_0_20px_rgba(139,35,50,0.8)]">
                Production
              </span>
            </h1>
            <p className="text-2xl text-white leading-relaxed max-w-3xl mx-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
              Professional video production, photography, and editing that elevates your brand. From concept to delivery, we create content that captivates and converts.
            </p>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient1)" />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* What's Included Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              What's{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Included
              </span>
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Full-service media production — from initial concept to final delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-8 border-2 border-brand-olive/30 hover:border-brand-maroon/50 transition-all duration-300 hover:scale-105 shadow-2xl"
              >
                <div className="w-16 h-16 bg-brand-maroon/20 rounded-xl flex items-center justify-center mb-6 text-brand-maroon">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                  {feature.title}
                </h3>
                <p className="text-brand-olive font-semibold mb-3 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {feature.description}
                </p>
                <p className="text-white/80 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {feature.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient2)" />
          <defs>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Process Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-brand-brown/10">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Process
              </span>
            </h2>
            <p className="text-xl text-white/80">
              From concept to final cut — here's how we bring your vision to life.
            </p>
          </div>

          <div className="space-y-8">
            {process.map((item, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-brand-brown/40 to-black/60 backdrop-blur-xl rounded-2xl p-8 border border-brand-olive/30 hover:border-brand-maroon/50 transition-all duration-300 shadow-lg flex items-start gap-6"
              >
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-brand-maroon to-brand-olive rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  {item.step}
                </div>
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold text-white mb-3 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                    {item.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits & Ideal For Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Benefits */}
            <div className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-10 border-2 border-brand-maroon/50 shadow-2xl">
              <div className="flex items-center gap-3 mb-8">
                <Zap className="w-10 h-10 text-brand-maroon" />
                <h3 className="text-3xl font-bold text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]">
                  Why It{' '}
                  <span className="text-brand-maroon">Works</span>
                </h3>
              </div>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-brand-maroon flex-shrink-0 mt-1" />
                    <span className="text-white/90 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ideal For */}
            <div className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-10 border-2 border-brand-olive/50 shadow-2xl">
              <div className="flex items-center gap-3 mb-8">
                <Camera className="w-10 h-10 text-brand-olive" />
                <h3 className="text-3xl font-bold text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]">
                  Ideal{' '}
                  <span className="text-brand-olive">For</span>
                </h3>
              </div>
              <ul className="space-y-4">
                {idealFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ArrowRight className="w-6 h-6 text-brand-olive flex-shrink-0 mt-1" />
                    <span className="text-white/90 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-brand-brown/10">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-gradient-to-br from-brand-maroon/20 to-brand-olive/20 backdrop-blur-xl rounded-3xl p-12 border-2 border-brand-maroon/50 shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Ready to Create{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Professional Content?
              </span>
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Let's bring your vision to life with high-quality video, photography, and media production that drives results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToContact}
                className="bg-brand-maroon hover:bg-brand-maroon/90 text-white font-bold text-lg px-12 py-6 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-brand-maroon/50"
              >
                Book Free Production Consultation
              </Button>
              <Button
                onClick={() => navigate('/packages/basic')}
                variant="outline"
                className="border-2 border-brand-olive text-brand-olive hover:bg-brand-olive hover:text-black font-bold text-lg px-12 py-6 rounded-xl transition-all duration-300"
              >
                View Pricing Packages
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MediaProductionPage;
